import { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface IInlineEditGroup {
  /** i18n id of the field currently in edit mode, or null when every row shows its value. */
  openKey: string | null
  /** Whether the open editor holds changes the user has not saved. */
  isDirty: boolean
  /**
   * Whether the open editor's draft could be saved. Separate from `isDirty` on purpose: a cleared
   * required field is a change worth warning about on the way out, and one Save must refuse.
   */
  isValid: boolean
  /** Opens `key`. Returns false when another row is holding unsaved changes. */
  requestOpen: (key: string) => boolean
  close: () => void
  setDirty: (dirty: boolean) => void
  setValid: (valid: boolean) => void
}

const NOOP_GROUP: IInlineEditGroup = {
  openKey: null,
  isDirty: false,
  isValid: true,
  requestOpen: () => false,
  close: () => undefined,
  setDirty: () => undefined,
  setValid: () => undefined,
}

export const InlineEditGroupContext = createContext<IInlineEditGroup>(NOOP_GROUP)

export const useInlineEditGroup = (): IInlineEditGroup => useContext(InlineEditGroupContext)

/**
 * One editor open at a time, and a clean one closes without a prompt.
 *
 * The state lives in a hook rather than inside the provider component so the page rendering the
 * provider can still read `isDirty` - it needs it for the navigation guard, and a component cannot
 * consume the context it provides.
 */
export function useInlineEditGroupState(): IInlineEditGroup {
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  // A row that never reports validity is valid; only a field with a rule has to opt in.
  const [isValid, setIsValid] = useState(true)

  const requestOpen = useCallback(
    (key: string) => {
      if (openKey === key) return true
      if (openKey && isDirty) return false
      setOpenKey(key)
      setIsDirty(false)
      setIsValid(true)
      return true
    },
    [openKey, isDirty]
  )

  const close = useCallback(() => {
    setOpenKey(null)
    setIsDirty(false)
    setIsValid(true)
  }, [])

  // This object is the provider value, so a fresh one each render re-renders every row.
  return useMemo(() => ({ openKey, isDirty, isValid, requestOpen, close, setDirty: setIsDirty, setValid: setIsValid }), [openKey, isDirty, isValid, requestOpen, close])
}
